// @ts-strict-ignore
import { atom, useRecoilTransactionObserver_UNSTABLE, selector } from 'recoil';
import type { RecoilState, MutableSnapshot, AtomEffect } from 'recoil';
import AppStorage from './AppStorage';
import type { APILoginInfoResponse } from '@/pages/LoginPage/api';

// Register all of the atom in this Collection.
// This is good for lookup like retrieving the persisted state then setting
// again the value inside the `initialState` of the RecoilRoot.
const atomCollection = new Map<string, RecoilState<any>>();

const atomKeys = {
    measurementPeriodAtom: 'measurementPeriodAtom',
    measurementPeriodSelector: 'measurementPeriodSelector',
    loggedUserAtom: 'loggedUserAtom',
    loginRememberedUsernameAtom: 'loginRememberedUsernameAtom',
    loggedUsers: 'loggedUsers',
};

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// login
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

const defaultUser = {
    username: '',
    firstName: '', // really?
    fullName: '', // really?
    hasPassword: false,
    rememberMe: false,
    password: {
        enabled: false,
        exists: false,
    },
    sso: {
        enabled: false,
        idp: '',
        companyName: '',
        domain: '',
    },
};

export const atomsStorageEffect =
    <T>(key: string): AtomEffect<T> =>
    ({ setSelf, onSet }) => {
        const savedValue = atomsStorage.getItem(key);
        if (savedValue != null) {
            setSelf(savedValue);
        }

        onSet((newValue, _, isReset) => {
            isReset
                ? atomsStorage.removeItem(key)
                : atomsStorage.setItem(key, newValue);
        });
    };

/**
 * This is used to remember users that logged in, so that logging in can be made simpler by just selecting from the list users. To start with, we
 * only will ever remember one user.
 */
export type RememberedLogin = {
    email: string;
    fullName: string;
};
export const REMEMBERED_LOGIN_ATOMS = 'remembered_logins';
export const rememberedLoginsAtom = atom<ReadonlyArray<RememberedLogin>>({
    key: REMEMBERED_LOGIN_ATOMS,
    default: [],
    effects: [atomsStorageEffect(REMEMBERED_LOGIN_ATOMS)],
});

const loggedUserAtom = atom<
    {
        username: string;
        fullName?: string;
        hasPassword?: boolean;
        rememberMe: boolean;
    } & APILoginInfoResponse['data']
>({
    key: atomKeys.loggedUserAtom,
    default: defaultUser,
});
atomCollection.set(atomKeys.loggedUserAtom, loggedUserAtom);

const loginRememberedUsernameAtom = selector({
    key: atomKeys.loginRememberedUsernameAtom,
    get({ get }) {
        const user = get(loggedUserAtom);
        if (!user.rememberMe) {
            return '';
        }
        return user.username;
    },
});

// Holding the state for progress bar. Use for showing progress
// bar whenever there is a request inside login.
const loginIsLoadingAtom = atom({
    key: 'loginIsLoading',
    default: false,
});

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// measurementPeriodAtom
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

export type MeasurementPeriodType =
    | '6-months'
    | '12-months'
    | '18-months'
    | '24-months'
    | 'custom-months';
const DEFAULT_MEASURING_PERIOD: {
    type: MeasurementPeriodType;
    fromDate: string | null;
    toDate: string | null;
} = {
    type: '12-months',
    fromDate: null,
    toDate: null,
};

const measurementPeriodAtom = atom({
    key: atomKeys.measurementPeriodAtom,
    default: DEFAULT_MEASURING_PERIOD,
});
atomCollection.set(atomKeys.measurementPeriodAtom, measurementPeriodAtom);

const measurementPeriodSelector = selector({
    key: atomKeys.measurementPeriodSelector,
    get: ({ get }) => {
        const currentDate = new Date();
        const measurementPeriod = get(measurementPeriodAtom);

        // first day of the month for calculating range.
        const fromDate =
            measurementPeriod.fromDate &&
            measurementPeriod.type === 'custom-months'
                ? new Date(measurementPeriod.fromDate)
                : initFromDate(currentDate, measurementPeriod.type);

        // last day of the month for calculating range.
        const toDate =
            measurementPeriod.toDate &&
            measurementPeriod.type === 'custom-months'
                ? new Date(measurementPeriod.toDate)
                : initToDate(currentDate);

        return {
            fromDate,
            toDate,
            type: measurementPeriod.type,
        };
    },
});

export function initFromDate(
    currentDate: Date,
    periodType: MeasurementPeriodType,
) {
    return new Date(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth() - getMonthsPeriod(periodType),
        // Pass 1 to correctly set the day into the first day of the current month.
        1,
        0,
        0,
        0,
        0,
    );
}

export function initToDate(currentDate: Date) {
    return new Date(
        currentDate.getUTCFullYear(),
        // Add 1 to correctly set the day into the last day of the current month.
        currentDate.getUTCMonth() + 1,
        0,
        0,
        0,
        0,
        0,
    );
}

/**
 * A function to change the "old" measurement period value in the storage to the "newly" supported type.
 */
function changeMeasurementPeriodValueType(period: number) {
    switch (period) {
        case 6: {
            return {
                type: '6-months',
                fromDate: null,
                toDate: null,
            };
        }
        case 12: {
            return {
                type: '12-months',
                fromDate: null,
                toDate: null,
            };
        }
        case 18: {
            return {
                type: '18-months',
                fromDate: null,
                toDate: null,
            };
        }
        // Default to 24 months.
        default: {
            return {
                type: '24-months',
                fromDate: null,
                toDate: null,
            };
        }
    }
}

/**
 * Get months period based on period type. This is use for date range.
 */
export function getMonthsPeriod(periodType: MeasurementPeriodType) {
    switch (periodType) {
        case '6-months': {
            return 6;
        }
        case '12-months': {
            return 12;
        }
        case '18-months': {
            return 18;
        }
        case '24-months': {
            return 24;
        }
        default: {
            // Default to custom months.
            return 0;
        }
    }
}

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// RecoilObserver
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

// init the storage
export const atomsStorage = new AppStorage();

/**
 * We can use this `RecoilObserver` for persisting, debugging, etc.
 * In the future, we need to have better solution on how to handle different
 * transactions. But for now, we gonna use it for persisting.
 * */
function RecoilObserver(_props: Record<string, never>) {
    // https://recoiljs.org/docs/guides/persistence#restoring-state
    useRecoilTransactionObserver_UNSTABLE(({ snapshot: { getLoadable } }) => {
        // TODO: Read the value before persisting.
        // This value must be serialize not Error object or etc.
        atomsStorage.setItem(
            measurementPeriodAtom.key,
            getLoadable(measurementPeriodAtom).contents,
        );
        atomsStorage.setItem(
            loggedUserAtom.key,
            getLoadable(loggedUserAtom).contents,
        );
    });

    return null;
}

function initialiseRecoilAtoms({ set }: MutableSnapshot) {
    Object.keys(atomKeys).forEach((key) => {
        const atom = atomCollection.get(key);
        const value = atomsStorage.getItem(key);
        // Initialise the atom by the persisted state.
        if (value != null && atom) {
            // TODO:
            // Handle measurement period. In the future, we gonna move this logic outside this function
            // to avoid specific logic coupling.
            if (
                atomKeys.measurementPeriodAtom === key &&
                typeof value === 'number'
            ) {
                // In the user's device, the old value of `measurementPeriod` in the local store is a number.
                // Because of the new "custom date" feature of measurement period, "number" value is not enough.
                // The new type of the `measurementPeriod` is object. We need to consider the "number" old value because
                // without consideration, then the "number" value will be used as the "initial" state for measurement period
                // which would case application error because the "expected" type must be "object" but it uses "number".
                // We will set to `undefined` all of the "number" value and let recoil atom resets the new "init" state based on the new type.
                set(atom, changeMeasurementPeriodValueType(value));
            } else {
                set(atom, value);
            }
        }
    });
}

export {
    measurementPeriodAtom,
    measurementPeriodSelector,
    defaultUser,
    loggedUserAtom,
    loginRememberedUsernameAtom,
    loginIsLoadingAtom,
    atomKeys,
    RecoilObserver,
    initialiseRecoilAtoms,
};
